import { useCallback, useEffect } from 'react';

import { usePageStateValueWithDefault } from '../stores/PageStateStore';
import { getStateFromUrl } from './PageStateUrlUtilities';
import { useLocalStorage } from './StorageUtilities';

function getUrlChartDisplayOption(pageStateKey) {
    const chartKey = pageStateKey.substring(pageStateKey.indexOf('-') + 1);
    const urlState = getStateFromUrl(pageStateKey);

    return urlState ? urlState[chartKey] : null;
}

/**
 * Gets the chart chart display type from the URL/local storage and a setter to update the display type
 * @param {string} pageStateKey
 * @param {string} defaultType
 * @returns {[string, () => void]} the chart display type for a page state key and a function to update it
 */
export default function useChartDisplayType(pageStateKey, defaultType) {
    const [stickyState, setStickyState, removeStickyState] = useLocalStorage(pageStateKey);
    const [value, setValue] = usePageStateValueWithDefault(pageStateKey, defaultType);

    useEffect(() => {
        const urlOption = getUrlChartDisplayOption(pageStateKey);

        // If the option passed on the URL is the default, remove any sticky state and update value to the default.
        // In this case we don't want anything set in the URL or local storage for this chart display
        if (urlOption === defaultType) {
            setValue(defaultType);
            removeStickyState();
        }
        // Otherwise, if non-default value is being pass via URL then set it on local storage if it hasn't already been
        else if (value !== defaultType && value !== stickyState) {
            setStickyState(value);
        }
        // Otherwise, if sticky state is present but not set on the URL, run setValue to update the URL with the
        // sticky display type.
        // This is to ensure that the panel displays correctly when the dashboard is viewed via a shared link.
        else if (stickyState) {
            setValue(stickyState);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setChartDisplayType = useCallback(
        (displayType) => {
            if (displayType !== defaultType) {
                setStickyState(displayType);
            } else {
                removeStickyState();
            }

            setValue(displayType);
        },
        [setStickyState, setValue, defaultType, removeStickyState]
    );

    return [value, setChartDisplayType];
}
